import { useState } from 'react';
import { PlusCircleIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { GenericTable, Spinner } from '@/modules/common';
import { CreateGcrDialog } from '@/modules/gcrs/components';

import { EventModelPosition, EventPrinterStatus } from "../../../constants";
import { AddEditModelDialog } from './components/add-edit-model-dialog';
import { useModels } from './hooks/use-models';

export const defaultFormValues = {
  models: [
    {
      modelId: '',
    },
  ],
  printers: [
    {
      name: '',
      printerId: '',
      type: '',
      status: EventPrinterStatus.Enabled,
    },
  ],
};

export type ModelDataType = {
  modelId: string;
  position: EventModelPosition[];
  sizeId: string;
  quantity: number;
};

export function ModelsTab() {
  const [selectedModel, setSelectedModel] = useState<ModelDataType | null>(
    null
  );
  const [addModelDialogOpen, setAddModelDialogOpen] = useState(false);

  const {
    columns,
    fields,
    createGcrDetails,
    isFetching,
    modelOptions,
    sizeOptions,
    refetchGcrs,
    setCreateGcrDetails,
    append,
    update,
  } = useModels();

  return (
    <div>
      {isFetching ? (
        <Spinner />
      ) : (
        <div>
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Models Table</h3>
            <Button
              disabled={isFetching}
              data-testid="add-model-button"
              onClick={() => setAddModelDialogOpen(true)}
              startIcon={<PlusCircleIcon size={16} />}
            >
              Add model
            </Button>
          </div>
          <GenericTable
            columns={columns}
            data={fields}
            key="models-table"
            name="models"
            emptyCellClassName="h-20"
            tableCellClassName="py-1"
            tableRowClassName="hover:bg-transparent"
            emptyTitle="No models yet"
            emptyDescription="Start building your models."
            onClick={(item: ModelDataType) => {
              setSelectedModel(item);
              setAddModelDialogOpen(true);
            }}
            disabledColumnsIdsToClick={[
              'name',
              'sizeId',
              'position',
              'quantity',
              'gcrFile',
              'actions',
            ]}
            emptyButton={
              <Button
                disabled={isFetching}
                data-testid="add-model-button"
                onClick={() => setAddModelDialogOpen(true)}
                startIcon={<PlusCircleIcon size={16} />}
              >
                Add model
              </Button>
            }
          >
            <p className="text-zinc-400 text-sm">All models: {fields.length}</p>
          </GenericTable>
        </div>
      )}

      {createGcrDetails && (
        <CreateGcrDialog
          isOpen
          onSuccess={() => refetchGcrs()}
          onOpenChange={() => setCreateGcrDetails(null)}
          {...createGcrDetails}
        />
      )}
      <AddEditModelDialog
        isOpen={addModelDialogOpen}
        onOpenChange={(open) => {
          setAddModelDialogOpen(open);
          setSelectedModel(null);
        }}
        modelOptions={modelOptions}
        sizeOptions={sizeOptions}
        selectedModel={selectedModel}
        onAdd={append}
        onEdit={(model) => {
          const index = fields.findIndex(
            (field) => field.modelId === selectedModel?.modelId
          );
          if (index !== -1) {
            update(index, model);
          }
        }}
      />
    </div>
  );
}
